import type { CalibrationRecord } from '@ctcells/contract'
import { localeDateTimeFormat, t } from '@ctcells/i18n'

import { DISPLAY_TIME_ZONE } from '../format.ts'

const DATE = localeDateTimeFormat({
  month: '2-digit',
  day: '2-digit',
  timeZone: DISPLAY_TIME_ZONE,
})

/**
 * `09-12` — 날짜만. 보정 유효기한은 시각이 의미 없다.
 * (`format.ts` 의 `dateTimeText` 는 시:분까지 붙어 표가 시끄러워진다.)
 */
export function dateText(value: string | null | undefined): string {
  if (value == null || value === '') return t('mask.none')
  const ms = Date.parse(value)
  if (Number.isNaN(ms)) return t('mask.none')
  /* 조각으로 짓는다 — 언어마다 구분자가 달라 글자 치환으로는 같은 꼴이 안 난다(`09. 12.` · `09/12`) */
  const parts = new Map(
    DATE()
      .formatToParts(new Date(ms))
      .map((part) => [part.type, part.value]),
  )
  return `${parts.get('month') ?? ''}-${parts.get('day') ?? ''}`
}

/** 보정 만료가 임박했다고 보는 기간. 🔸 서버가 판정해 주면 그 값을 쓴다 — 지금은 표시 규칙이다 */
const EXPIRY_WARNING_DAYS = 30

/**
 * 이 유효기한이 임박했는가. **모르면 `false`** 다 — 기한을 모르는 것을 「곧 만료」로 굵게
 * 칠하면 없는 사실을 그리는 것이 된다.
 *
 * 🔴 **판정은 `== null` 하나다.** 서버가 값 없는 칸을 키째 빼기 때문이다(아래 머리말 참조).
 * 🔴 **표가 자기 기준일을 따로 두지 않게** 여기로 모았다 — 종전에는 「30일」이 이 파일과
 *    모듈 상세 표 두 곳에 있어, 한쪽만 고치면 같은 화면이 두 기준으로 굵어질 수 있었다.
 */
export function isExpiringSoon(validUntil: string | null | undefined, now: string): boolean {
  if (validUntil == null || validUntil === '') return false
  const due = Date.parse(validUntil)
  if (Number.isNaN(due)) return false
  return due <= Date.parse(now) + EXPIRY_WARNING_DAYS * 24 * 60 * 60 * 1000
}

export interface CalibrationStatus {
  /** 가장 이른 다음 권장일 */
  nextText: string | null
  expiring: boolean
}

/**
 * 모듈의 보정 상태 한 줄.
 *
 * ⚠ **표시 전용 판정이다.** 시작 게이트(SVC-SCH-07)는 서버가 `allowed_commands` 로 집행한다 —
 *   이 값으로 버튼을 막지 않는다.
 */
export function calibrationStatusOf(
  moduleId: string,
  calibrations: readonly CalibrationRecord[],
  now: string,
): CalibrationStatus {
  /*
   * 🔴 **`!== null` 이 아니라 `!= null` 이다**(2026-09-20 실측으로 고쳤다).
   *    서버는 값이 없는 칸을 `null` 로 «싣지 않고» **키째 뺀다**(`model_dump(exclude_none=True)`)
   *    — 실물 응답에 `valid_until` 이 아예 없는 보정 행을 확인했다(`gripper_offset`·유효기한 미기재).
   *    ⇒ 종전 `!== null` 은 그 행을 «유효기한 있음»으로 통과시켜, 다음 권장일이 없는 모듈에
   *    `—` 를 다음 권장일로 세우고 있었다. 목에는 항상 값이 있어 드러나지 않던 자리다.
   *    ⚠ 종전에는 화면 타입이 `Required<CalibrationRecord>` 라 **TS 가 이 부재를 한 건도
   *      못 잡았다.** 2026-09-20 그 좁히기를 걷었다(`packages/contract/src/module.ts`) —
   *      이제 `c.valid_until` 은 `string | null | undefined` 라 타입이 같이 지킨다.
   */
  const dues = calibrations
    .filter((c) => c.target === moduleId)
    .map((c) => c.valid_until)
    .filter((due): due is string => due != null && due !== '')
  if (dues.length === 0) return { nextText: null, expiring: false }

  const earliest = [...dues].sort((a, b) => a.localeCompare(b))[0] as string

  return { nextText: dateText(earliest), expiring: isExpiringSoon(earliest, now) }
}
